import type { ChainModifiers, Entry, EntryFieldTypes, EntrySkeletonType, LocaleCode } from "contentful";
import type { TypeSmallCtaSkeleton } from "./TypeSmallCta";
import type { TypeTableOfContentsSettingSkeleton } from "./TypeTableOfContentsSetting";

/**
 * Fields type definition for content type 'TypeHeroCardSection'
 * @name TypeHeroCardSectionFields
 * @type {TypeHeroCardSectionFields}
 * @memberof TypeHeroCardSection
 */
export interface TypeHeroCardSectionFields {
    /**
     * Field type definition for field 'internalTitle' (Internal Title)
     * @name Internal Title
     * @localized false
     */
    internalTitle?: EntryFieldTypes.Symbol;
    /**
     * Field type definition for field 'header' (Header)
     * @name Header
     * @localized true
     */
    header: EntryFieldTypes.Symbol;
    /**
     * Field type definition for field 'subHeader' (Sub Header)
     * @name Sub Header
     * @localized true
     */
    subHeader?: EntryFieldTypes.Symbol;
    /**
     * Field type definition for field 'description' (Description)
     * @name Description
     * @localized true
     */
    description: EntryFieldTypes.Text;
    /**
     * Field type definition for field 'cta' (CTA)
     * @name CTA
     * @localized true
     */
    cta?: EntryFieldTypes.EntryLink<TypeSmallCtaSkeleton>;
    /**
     * Field type definition for field 'image' (Image)
     * @name Image
     * @localized false
     */
    image?: EntryFieldTypes.AssetLink;
    /**
     * Field type definition for field 'backgroundColor' (Background Color)
     * @name Background Color
     * @localized false
     */
    backgroundColor: EntryFieldTypes.Symbol<"blue" | "dark-blue" | "gold" | "green" | "grey" | "light-blue" | "magenta" | "red" | "white" | "yellow">;
    /**
     * Field type definition for field 'cardAlignment' (Card Alignment)
     * @name Card Alignment
     * @localized false
     */
    cardAlignment: EntryFieldTypes.Symbol<"Left" | "Right">;
    /**
     * Field type definition for field 'shareCta' (Share CTA)
     * @name Share CTA
     * @localized false
     */
    shareCta?: EntryFieldTypes.Boolean;
    /**
     * Field type definition for field 'tocSetting' (tocSetting)
     * @name tocSetting
     * @localized false
     */
    tocSetting?: EntryFieldTypes.EntryLink<TypeTableOfContentsSettingSkeleton>;
    /**
     * Field type definition for field 'shadowColor' (Shadow Color)
     * @name Shadow Color
     * @localized false
     */
    shadowColor?: EntryFieldTypes.Symbol<"blue" | "dark-blue" | "gold" | "green" | "light-blue" | "magenta" | "red" | "yellow">;
}

/**
 * Entry skeleton type definition for content type 'heroCardSection' (Hero Card Section)
 * @name TypeHeroCardSectionSkeleton
 * @type {TypeHeroCardSectionSkeleton}
 * @author 7BjsgI9gzpO6Dv7BH5AOT6
 * @since 2018-03-28T12:49:18.104Z
 * @version 30
 */
export type TypeHeroCardSectionSkeleton = EntrySkeletonType<TypeHeroCardSectionFields, "heroCardSection">;
/**
 * Entry type definition for content type 'heroCardSection' (Hero Card Section)
 * @name TypeHeroCardSection
 * @type {TypeHeroCardSection}
 * @author 7BjsgI9gzpO6Dv7BH5AOT6
 * @since 2018-03-28T12:49:18.104Z
 * @version 30
 */
export type TypeHeroCardSection<Modifiers extends ChainModifiers, Locales extends LocaleCode = LocaleCode> = Entry<TypeHeroCardSectionSkeleton, Modifiers, Locales>;

export function isTypeHeroCardSection<Modifiers extends ChainModifiers, Locales extends LocaleCode>(entry: Entry<EntrySkeletonType, Modifiers, Locales>): entry is TypeHeroCardSection<Modifiers, Locales> {
    return entry.sys.contentType.sys.id === 'heroCardSection'
}
